import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { FileText, Loader2, CheckCircle, Clock, Download, PenLine } from 'lucide-react';

export default function DocumentsPage() {
  const { api } = useAuth();
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);

  const downloadPdf = async (sigId, title, parentName, learnerName) => {
    try {
      const r = await api.get(`/documents/signatures/${sigId}/download/`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      const parts = [title, parentName, learnerName].filter(Boolean).join('_').replace(/\s+/g, '_');
      a.download = `${parts || 'signed-document'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  useEffect(() => {
    api.get('/documents/my-signatures/')
      .then(r => setSignatures(r.data))
      .catch(() => toast.error('Failed to load documents'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Agreement Documents</h1>
        <p className="text-muted-foreground">Documents you have signed for your learners.</p>
      </div>

      {signatures.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-1">No documents yet</h3>
            <p className="text-muted-foreground text-sm">
              When a session requires an agreement, it will appear here for you to sign.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {signatures.map(sig => (
            <Card key={sig.id}>
              <CardContent className="flex items-center gap-4 py-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  {sig.completed_at ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <Clock className="h-5 w-5 text-amber-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{sig.document_title}</p>
                  {sig.learner_name && (
                    <p className="text-sm text-muted-foreground">For: {sig.learner_name}</p>
                  )}
                  {sig.completed_at && (
                    <p className="text-xs text-muted-foreground">
                      Signed {new Date(sig.completed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant={sig.completed_at ? 'default' : 'secondary'}>
                    {sig.completed_at ? 'Signed' : 'Incomplete'}
                  </Badge>
                  {sig.signed_pdf_url && (
                    <Button size="sm" variant="outline" onClick={() => downloadPdf(sig.id, sig.document_title, sig.parent_print_name, sig.learner_name)}>
                      <Download className="h-3.5 w-3.5 mr-1" /> PDF
                    </Button>
                  )}
                  {!sig.completed_at && (
                    <Link to={`/parent/documents/${sig.document}/sign${sig.learner ? `?learner=${sig.learner}` : ''}`}>
                      <Button size="sm">
                        <PenLine className="h-3.5 w-3.5 mr-1" /> Sign
                      </Button>
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
